import { createAiRequestTelemetry } from "../ai/request-telemetry";
import { DefaultChatTransport, JSONParseError, TypeValidationError } from "ai";
import { payloadHash } from "./request-hash";
import { aiAuthHeaders } from "../ai/auth-header";
import type { AssistantPayload } from "./types";
import { isAssistantProgress, type AssistantProgress } from "./progress";
import { AssistantClientError, isAssistantClientError } from "./client-error";
import { reportAssistantClientFailure } from "./client-telemetry";
import { ASSISTANT_PROTOCOL_HEADER, ASSISTANT_PROTOCOL_VERSION, type AssistantFailure, type AssistantUIMessage, type CancelResponse, type SendResponse } from "./protocol";

export type { AssistantFailure, SendResponse, CancelResponse } from "./protocol";

export type AssistantQuote = { token: string; cost: number; remaining: number; expiresAt: string; access: "beta" | "included"; recovery?: boolean };
export type AssistantUsage = { remaining: number; allowance: number; maxCost: number };
export type UsageResponse = { ok: true; usage: AssistantUsage } | AssistantFailure;
export type QuoteResponse = { ok: true; quote: AssistantQuote } | AssistantFailure;
export type AssistantFeedback = "applied" | "dismissed" | "helpful" | "not_helpful";

function ensureCurrent(shouldContinue?: () => boolean) {
  if (shouldContinue && !shouldContinue()) throw new DOMException("Assistant request superseded", "AbortError");
}
async function beforeDeadline<T>(pending: Promise<T>, deadline: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new AssistantClientError("auth_timeout", "authentication", undefined, "Assistant authentication timed out")), Math.max(0, deadline - Date.now()));
  });
  // Promise.race observes late rejections too; an expired auth refresh must not
  // leave an unhandled rejection or allow a later request to escape the guard.
  try { return await Promise.race([pending, timeout]); }
  finally { clearTimeout(timer); }
}
/** Older Safari supports timeout() but not any(). Keep transport cancellation
 * compatible with those phones and release listeners when either signal ends. */
function requestSignal(timeoutMs: number, parent?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(timeoutMs);
  if (!parent) return timeout;
  if (parent.aborted) return parent;
  const controller = new AbortController();
  const finish = (reason: unknown) => {
    parent.removeEventListener("abort", onParent);
    timeout.removeEventListener("abort", onTimeout);
    controller.abort(reason);
  };
  const onParent = () => finish(parent.reason);
  const onTimeout = () => finish(timeout.reason);
  parent.addEventListener("abort", onParent, { once: true });
  timeout.addEventListener("abort", onTimeout, { once: true });
  return controller.signal;
}
async function request(body: unknown, timeoutMs: number, stream = false, shouldContinue?: () => boolean, telemetry?: ReturnType<typeof createAiRequestTelemetry>, signal?: AbortSignal): Promise<Response> {
  signal?.throwIfAborted();
  const deadline = Date.now() + timeoutMs;
  let auth: Awaited<ReturnType<typeof aiAuthHeaders>>;
  try { auth = await beforeDeadline(aiAuthHeaders(), deadline); }
  catch (error) {
    ensureCurrent(shouldContinue);
    if (isAssistantClientError(error)) throw error;
    throw new AssistantClientError("auth", "authentication");
  }
  // Authentication can refresh asynchronously while the user changes accounts.
  ensureCurrent(shouldContinue);
  const remaining = deadline - Date.now();
  if (remaining <= 0) throw new AssistantClientError("timeout", "request", undefined, "Assistant request timed out");
  try {
    return await fetch("/api/assistant", {
      method: "POST", headers: { "Content-Type": "application/json", ...(stream ? { Accept: "text/event-stream", [ASSISTANT_PROTOCOL_HEADER]: ASSISTANT_PROTOCOL_VERSION } : {}), ...auth, ...telemetry?.headers },
      body: JSON.stringify(body), signal: requestSignal(remaining, signal),
    });
  } catch (error) {
    throw new AssistantClientError(isTimeout(error, deadline) ? "timeout" : "network", "request");
  }
}
const isTimeout = (error: unknown, deadline = Infinity) => Date.now() >= deadline || (error instanceof Error && error.name === "TimeoutError");
async function readJson(response: Response, deadline = Infinity): Promise<unknown> {
  try { return await response.json(); }
  catch (error) {
    throw new AssistantClientError(isTimeout(error, deadline) ? "timeout" : !response.ok ? "http" : error instanceof SyntaxError ? "invalid_response" : "network", "response", response.status);
  }
}
async function post(body: unknown, timeoutMs = 15_000, shouldContinue?: () => boolean): Promise<{ data: unknown; status: number }> {
  const deadline = Date.now() + timeoutMs;
  const response = await request(body, timeoutMs, false, shouldContinue);
  const data = await readJson(response, deadline);
  if (!data || typeof data !== "object" || !("ok" in data) || typeof data.ok !== "boolean" ||
      (!data.ok && (!("reason" in data) || typeof data.reason !== "string"))) throw new AssistantClientError(!response.ok ? "http" : "invalid_response", "response", response.status, "Invalid assistant response");
  return { data, status: response.status };
}
export async function quoteAssistant(payload: AssistantPayload, shouldContinue?: () => boolean): Promise<QuoteResponse> {
  try {
    const hash = await payloadHash(payload);
    ensureCurrent(shouldContinue);
    const { data, status } = await post({ action: "quote", hash, mode: payload.mode, projectId: payload.projectId ?? null, requestId: payload.requestId }, 15_000, shouldContinue);
    ensureCurrent(shouldContinue);
    const result = data as QuoteResponse;
    if (result.ok && (!result.quote || typeof result.quote.token !== "string" || !Number.isFinite(result.quote.cost) || result.quote.cost < 0 || !Number.isFinite(result.quote.remaining) || !Number.isFinite(Date.parse(result.quote.expiresAt)) || !["beta", "included"].includes(result.quote.access))) throw new AssistantClientError("invalid_response", "response", status, "Invalid quote");
    return result;
  } catch (error) {
    ensureCurrent(shouldContinue);
    reportAssistantClientFailure("quote", payload.requestId, 1, error);
    throw error;
  }
}
export async function getAssistantUsage(): Promise<UsageResponse> {
  const { data } = await post({ action: "usage" });
  const result = data as UsageResponse;
  if (result.ok && (!result.usage || !Number.isFinite(result.usage.remaining) || result.usage.remaining < 0 || !Number.isFinite(result.usage.allowance) || !Number.isFinite(result.usage.maxCost) || result.usage.maxCost <= 0)) throw new Error("Invalid usage");
  return result;
}
// Bound the whole stream, including unfinished events, without buffering it all.
const MAX_STREAM_BYTES = 2_000_000;
class AssistantProtocolError extends AssistantClientError {
  constructor(message: string, status: number) { super("protocol", "stream", status, message); }
}
/** Use the SDK's complete UI-message protocol decoder. Request authentication,
 * JSON reservation/replay responses and Crimp's recovery identity stay shared
 * with the other assistant endpoints, outside the transport's chat history. */
class AssistantChatTransport extends DefaultChatTransport<AssistantUIMessage> {
  readStream(stream: ReadableStream<Uint8Array>) {
    return this.processResponseStream(stream);
  }
}
const chatTransport = new AssistantChatTransport();
async function readAssistantStream(response: Response, onProgress?: (progress: AssistantProgress) => void, deadline = Infinity): Promise<unknown> {
  if (!response.body) throw new AssistantClientError("interrupted_stream", "stream", response.status, "Assistant stream unavailable");
  let received = 0;
  const bounded = response.body.pipeThrough(new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      received += chunk.byteLength;
      if (received > MAX_STREAM_BYTES) throw new AssistantProtocolError("Assistant stream too large", response.status);
      controller.enqueue(chunk);
    },
  }));
  const reader = chatTransport.readStream(bounded).getReader();
  try {
    while (true) {
      const { value, done } = await reader.read();
      // A valid SDK envelope is not proof that a reply settled. Only the
      // domain result completes the request; EOF/abort/error remains replayable.
      if (done || value.type === "finish" || value.type === "abort" || value.type === "error") {
        throw new AssistantClientError("interrupted_stream", "stream", response.status, "Assistant stream ended before its result");
      }
      if (value.type === "data-assistant-result") return value.data;
      if (value.type === "data-assistant-progress" && isAssistantProgress(value.data)) {
        const { phase, detail } = value.data;
        onProgress?.({ phase, ...(detail ? { detail } : {}) });
      }
    }
  } catch (error) {
    if (isAssistantClientError(error)) throw error;
    if (JSONParseError.isInstance(error) || TypeValidationError.isInstance(error)) throw new AssistantProtocolError("Invalid assistant stream event", response.status);
    throw new AssistantClientError(isTimeout(error, deadline) ? "timeout" : "interrupted_stream", "stream", response.status);
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
function validateSendResponse(data: unknown, payload: AssistantPayload, status: number): SendResponse {
  if (!data || typeof data !== "object" || !("ok" in data) || typeof data.ok !== "boolean" ||
      (!data.ok && (!("reason" in data) || typeof data.reason !== "string"))) throw new AssistantClientError(status >= 400 ? "http" : "invalid_response", "response", status, "Invalid assistant response");
  const result = data as SendResponse;
  if (result.ok && (result.requestId !== payload.requestId || !result.reply || typeof result.reply.message !== "string" || !Array.isArray(result.reply.evidence) || !["answer", "clarification", "proposal", "unsupported"].includes(result.reply.kind) || !Number.isFinite(result.charged) || !Number.isFinite(result.remaining))) throw new AssistantClientError("invalid_response", "response", status, "Invalid reply");
  return result;
}
const SEND_TIMEOUT_MS = 240_000;
const RECOVERY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 30_000];
/** A request the server confirms is still running is polled until the send
 * deadline; a generation may take three minutes and the user is watching it. */
const IN_PROGRESS_POLL_MS = 10_000;
/** Show confirmed running activity or recovery after a dropped stream.
 * An unavailable response cannot tell us whether generation or settlement
 * happened, so retrying the same request is described as checking its status. */
function waitingProgress(result: SendResponse | null): AssistantProgress {
  if (result && !result.ok && result.reason === "in_progress" && isAssistantProgress(result.progress)) {
    const { phase, detail } = result.progress;
    return { phase, ...(detail ? { detail } : {}), ...(result.blockedBy === "earlier_request" ? { earlier: true } : {}) };
  }
  if (!result || result.ok) return { phase: "recovering" };
  return { phase: result.reason === "in_progress" ? "working" : "resending" };
}
export async function sendAssistant(payload: AssistantPayload, quoteToken: string, onProgress?: (progress: AssistantProgress) => void, shouldContinue?: () => boolean, signal?: AbortSignal): Promise<SendResponse> {
  const deadline = Date.now() + SEND_TIMEOUT_MS;
  const body = { action: "send", payload, quoteToken };
  const telemetry = createAiRequestTelemetry("assistant", payload.requestId);
  let lastStatus: number | null = null;
  const complete = (result: SendResponse): SendResponse => {
    telemetry.finish(result.ok ? "success" : ["in_progress", "unavailable", "retry_same_request", "failed", "expired"].includes(result.reason) ? "failed" : "denied", lastStatus);
    return result;
  };
  const progress = (value: AssistantProgress) => { if (!shouldContinue || shouldContinue()) onProgress?.(value); };
  let attempts = 0;
  // A transport interruption may hide a completed, paid reply. Replay only the
  // original request; the server's reservation decides whether it may run or
  // return its saved result. Never refresh a quote or invent a new request here.
  try {
    for (let attempt = 0; ; attempt++) {
      ensureCurrent(shouldContinue);
      const remaining = deadline - Date.now();
      if (remaining <= 0) throw new AssistantClientError("timeout", "request", undefined, "Assistant request timed out");
      attempts = attempt + 1;
      let data: unknown;
      let failure: unknown;
      let interrupted = false;
      let status = 0;
      lastStatus = null;
      try {
        const response = await request(body, remaining, true, shouldContinue, telemetry, signal);
        lastStatus = status = response.status;
        data = response.headers.get("Content-Type")?.toLowerCase().split(";", 1)[0].trim() === "text/event-stream"
          ? await readAssistantStream(response, progress, deadline)
          : await readJson(response, deadline);
      } catch (error) {
        ensureCurrent(shouldContinue);
        if (error instanceof AssistantProtocolError) throw error;
        failure = error;
        interrupted = true;
      }
      ensureCurrent(shouldContinue);
      // A complete malformed or mismatched reply is a protocol failure, not a
      // reason to keep issuing requests. Partial transport failures are retried.
      const result = interrupted ? null : validateSendResponse(data, payload, status);
      if (result && (result.ok || !["in_progress", "unavailable", "retry_same_request"].includes(result.reason))) return complete(result);
      const delay = RECOVERY_DELAYS_MS[attempt] ?? (result && !result.ok && result.reason === "in_progress" ? IN_PROGRESS_POLL_MS : undefined);
      if (delay === undefined || Date.now() + delay >= deadline) {
        if (result) return complete(result);
        throw failure;
      }
      progress(waitingProgress(result));
      await waitForRecovery(delay, signal);
    }
  } catch (error) {
    // Switching drawings/accounts intentionally supersedes a request. That is
    // neither a failure nor a reason to keep polling the old conversation.
    ensureCurrent(shouldContinue);
    telemetry.finish("failed", lastStatus);
    reportAssistantClientFailure("send", payload.requestId, attempts, error);
    throw error;
  }
}
/** Abort a suspended connection without waiting for its next poll or heartbeat. */
function waitForRecovery(delay: number, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const finish = () => { signal?.removeEventListener("abort", abort); resolve(); };
    const timer = setTimeout(finish, delay);
    const abort = () => { clearTimeout(timer); signal?.removeEventListener("abort", abort); reject(signal?.reason); };
    signal?.addEventListener("abort", abort, { once: true });
  });
}

/** Reconnect only to work the server already accepted. This never quotes,
 * reserves credits, resends a prompt or starts generation. */
export async function resumeAssistant(payload: AssistantPayload, onProgress?: (progress: AssistantProgress) => void, shouldContinue?: () => boolean, signal?: AbortSignal): Promise<SendResponse> {
  const hash = await payloadHash(payload);
  const deadline = Date.now() + SEND_TIMEOUT_MS;
  let failures = 0;
  let missingAttempts = 0;
  for (let attempt = 0; ; attempt++) {
    ensureCurrent(shouldContinue);
    signal?.throwIfAborted();
    const remaining = deadline - Date.now();
    if (remaining <= 0) return { ok: false, reason: "retry_same_request", connectionReason: "timeout" };
    let result: SendResponse;
    try {
      const response = await request({ action: "status", requestId: payload.requestId, hash }, Math.min(15_000, remaining), false, shouldContinue, undefined, signal);
      result = validateSendResponse(await readJson(response), payload, response.status);
      failures = 0;
    } catch (error) {
      ensureCurrent(shouldContinue);
      signal?.throwIfAborted();
      if (isAssistantClientError(error) && ["invalid_response", "protocol"].includes(error.reason)) throw error;
      if (++failures > RECOVERY_DELAYS_MS.length) throw error;
      onProgress?.({ phase: "recovering" });
      await waitForRecovery(RECOVERY_DELAYS_MS[failures - 1], signal);
      continue;
    }
    ensureCurrent(shouldContinue);
    // A send may not have reached the server before the phone went offline.
    // Only another explicit Send may submit that missing request.
    if (!result.ok && result.reason === "not_found") {
      // The original send can still be reserving its job when we reconnect.
      // Give that handoff time to finish, without ever submitting work here.
      const delay = [2_000, 4_000, 8_000][missingAttempts++];
      if (delay === undefined) return { ok: false, reason: "retry_same_request" };
      onProgress?.({ phase: "recovering" });
      await waitForRecovery(delay, signal);
      continue;
    }
    missingAttempts = 0;
    if (result.ok || result.reason !== "in_progress") return result;
    onProgress?.(waitingProgress(result));
    await waitForRecovery(attempt === 0 ? 2_000 : 4_000, signal);
  }
}

export async function sendAssistantFeedback(requestId: string, feedback: AssistantFeedback): Promise<boolean> {
  const { data } = await post({ action: "feedback", requestId, event: feedback });
  const result = data as { ok: boolean };
  return result.ok === true;
}

/** Explicit Stop is separate from closing a stream: disconnected browsers must
 * leave accepted jobs running, while this exact owner/hash cancels the job. */
export async function cancelAssistant(payload: AssistantPayload, shouldContinue?: () => boolean): Promise<CancelResponse> {
  const hash = await payloadHash(payload);
  ensureCurrent(shouldContinue);
  const { data, status } = await post({ action: "cancel", requestId: payload.requestId, hash }, 30_000, shouldContinue);
  ensureCurrent(shouldContinue);
  const result = data as CancelResponse;
  if (result.ok && (result.requestId !== payload.requestId || !["cancelled", "completed"].includes(result.state) ||
    !Number.isFinite(result.charged) || result.charged < 0 || (result.state === "cancelled" && result.charged !== 0) ||
    (result.remaining !== undefined && (!Number.isFinite(result.remaining) || result.remaining < 0)))) {
    throw new AssistantClientError("invalid_response", "response", status, "Invalid cancellation");
  }
  return result;
}
